/**
 * PdvDesktopAuthService
 *
 * Autenticação exclusiva do PDV Desktop (Electron).
 * Chama o mesmo endpoint /autenticacao da aplicação web,
 * mas persiste o token e os dados do usuário no SQLite local
 * via Electron IPC, sem tocar no Vuex/JwtService do app web.
 */
import { SYSTEM_VARIABLE } from "@/modules/Application";
import { isDesktopMode, getElectronAPI } from "@/plugins/desktop";

function _decodeJwtPayload(token) {
  if (!token || typeof atob !== "function") return null;

  try {
    const payload = String(token).split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch (_) {
    return null;
  }
}

function _parseId(value) {
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

const PdvDesktopAuthService = {
  /**
   * Autentica o usuário no servidor e salva o token localmente.
   *
   * @param {string} email
   * @param {string} senha
   * @returns {Promise<{ token: string, user: object, empresa: object }>}
   * @throws {Error} com mensagem amigável para exibir ao usuário
   */
  async login(email, senha) {
    const apiUrl = (SYSTEM_VARIABLE.BASE_URL_API || "").replace(/\/$/, "");
    if (!apiUrl) throw new Error("URL da API não configurada. Verifique a configuração do terminal.");

    let resp;
    try {
      resp = await fetch(`${apiUrl}/api/autenticacao`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ email, password: senha }),
      });
    } catch (_) {
      throw new Error("Não foi possível conectar ao servidor. Verifique sua conexão.");
    }

    let json = {};
    try { json = await resp.json(); } catch (_) { /* ignore */ }

    if (!resp.ok) {
      const msg =
        json.message ??
        json.msg ??
        json.error ??
        (resp.status === 401 ? "Usuário ou senha inválidos." : `Erro ${resp.status}.`);
      throw new Error(msg);
    }

    const token =
      json.token ??
      json.access_token ??
      json.data?.token ??
      null;

    if (!token) throw new Error("Token não retornado pelo servidor. Contate o suporte.");

    const user    = json.user    ?? json.data?.user    ?? json.data ?? {};
    const empresa = user.empresa ?? json.empresa       ?? {};

    if (isDesktopMode()) {
      const api = getElectronAPI();
      const idUsuario = user.id ?? user.id_usuario ?? "";
      const nomeUsuario = user.name ?? user.nome ?? email;
      await Promise.all([
        api.config.set("api_token",     token),
        api.config.set("usuario_id",    String(idUsuario)),
        api.config.set("usuario_nome",  nomeUsuario),
        api.config.set("usuario_email", user.email ?? email),
        api.config.set("id_empresa",    String(empresa.id ?? empresa.id_empresa ?? "")),
        // Chaves padrao usadas pelo PDV Desktop.
        api.config.set("id_usuario",    String(idUsuario)),
        api.config.set("nome_usuario",  nomeUsuario),
        api.config.set("ultimo_login_online", new Date().toISOString()),
      ]);

      // Salva credencial local para permitir login offline futuro.
      // A senha é armazenada apenas como hash seguro (scrypt) no SQLite.
      try {
        await api.usuarios.salvarCredencial({
          id_usuario: _parseId(idUsuario),
          email: user.email ?? email,
          login: user.usuario ?? user.login ?? user.email ?? email,
          nome: nomeUsuario,
          id_empresa: _parseId(empresa.id ?? empresa.id_empresa),
          senha,
          permissoes: user.permissoes ?? user.permissions ?? null,
          payload: { id_usuario: _parseId(idUsuario), nome: nomeUsuario, email: user.email ?? email },
          ativo: (user.ativo ?? user.active ?? 1) ? 1 : 0,
        });
      } catch (e) {
        // cache offline é best-effort; não bloqueia login online
        console.warn("[PdvDesktopAuth] Falha ao salvar credencial offline:", e?.message || e);
      }
    }

    return { token, user, empresa };
  },

  /**
   * Autentica o usuário usando o cache local (sem internet).
   * Só funciona se já houve um login online anterior neste terminal.
   *
   * @param {string} login email/usuário
   * @param {string} senha
   * @returns {Promise<{ usuario: object }>}
   * @throws {Error} mensagem amigável
   */
  async loginOffline(login, senha) {
    if (!isDesktopMode()) {
      throw new Error("Login offline disponível apenas no aplicativo de desktop.");
    }
    const api = getElectronAPI();

    const temCache = await api.usuarios.existeAlgum().catch(() => false);
    if (!temCache) {
      throw new Error("É necessário fazer o primeiro login com internet neste terminal.");
    }

    const res = await api.usuarios.validar(login, senha);
    if (!res || !res.ok) {
      switch (res && res.code) {
        case "SEM_CACHE":
          throw new Error("É necessário fazer o primeiro login com internet neste terminal.");
        case "INATIVO":
          throw new Error("Usuário inativo. Refaça o login com internet.");
        default:
          throw new Error("Usuário ou senha inválidos.");
      }
    }

    // Re-estabelece a sessão local para que os guards de rota liberem o PDV.
    // Como não há internet, geramos um token offline (apenas marcador local).
    const u = res.usuario || {};
    await Promise.all([
      api.config.set("api_token",     `offline:${u.id_usuario ?? ""}:${Date.now()}`),
      api.config.set("usuario_id",    String(u.id_usuario ?? "")),
      api.config.set("id_usuario",    String(u.id_usuario ?? "")),
      api.config.set("usuario_nome",  u.nome ?? ""),
      api.config.set("nome_usuario",  u.nome ?? ""),
      api.config.set("usuario_email", u.email ?? login),
      api.config.set("id_empresa",    String(u.id_empresa ?? "")),
    ]);

    return { usuario: res.usuario };
  },

  /**
   * Indica se há ao menos uma credencial salva para login offline.
   * @returns {Promise<boolean>}
   */
  async temCredencialOffline() {
    if (!isDesktopMode()) return false;
    try {
      return !!(await getElectronAPI().usuarios.existeAlgum());
    } catch (_) {
      return false;
    }
  },

  /**
   * Limpa a sessão local (logout).
   * Não faz chamada ao servidor — apenas remove o token local.
   */
  async logout() {
    if (!isDesktopMode()) return;
    const api = getElectronAPI();
    await Promise.all([
      api.config.set("api_token",     ""),
      api.config.set("usuario_id",    ""),
      api.config.set("usuario_nome",  ""),
      api.config.set("usuario_email", ""),
      api.config.set("id_usuario",    ""),
      api.config.set("nome_usuario",  ""),
    ]);
  },

  /**
   * Verifica se existe token salvo localmente.
   * @returns {Promise<boolean>}
   */
  async isAuthenticated() {
    if (!isDesktopMode()) return false;
    const token = await getElectronAPI().config.get("api_token");
    return !!token;
  },

  /**
   * Retorna o nome do usuário logado localmente.
   * @returns {Promise<string>}
   */
  async getNomeUsuario() {
    if (!isDesktopMode()) return "";
    const api = getElectronAPI();
    const [nomeUsuario, nomeLegado] = await Promise.all([
      api.config.get("nome_usuario").catch(() => ""),
      api.config.get("usuario_nome").catch(() => ""),
    ]);
    return nomeUsuario || nomeLegado || "";
  },

  /**
   * Retorna o e-mail do usuário logado localmente.
   * @returns {Promise<string>}
   */
  async getEmailUsuario() {
    if (!isDesktopMode()) return "";
    return (await getElectronAPI().config.get("usuario_email")) || "";
  },

  /**
   * Retorna o usuario atual salvo localmente.
   * @returns {Promise<{ id_usuario: number|null, nome: string, email: string, id_empresa: number|null }>}
   */
  async getUsuarioAtual() {
    if (!isDesktopMode()) {
      return { id_usuario: null, nome: "", email: "", id_empresa: null };
    }

    const api = getElectronAPI();
    const [idUsuarioRaw, idUsuarioLegado, nomeUsuario, nomeLegado, email, idEmpresaRaw, token] = await Promise.all([
      api.config.get("id_usuario").catch(() => null),
      api.config.get("usuario_id").catch(() => null),
      api.config.get("nome_usuario").catch(() => ""),
      api.config.get("usuario_nome").catch(() => ""),
      api.config.get("usuario_email").catch(() => ""),
      api.config.get("id_empresa").catch(() => null),
      api.config.get("api_token").catch(() => null),
    ]);

    const tokenPayload = _decodeJwtPayload(token);
    const idUsuarioFinal = idUsuarioRaw || idUsuarioLegado || tokenPayload?.sub || tokenPayload?.id_usuario || null;
    const idEmpresaFinal = idEmpresaRaw || null;

    return {
      id_usuario: idUsuarioFinal ? _parseId(idUsuarioFinal) : null,
      nome: nomeUsuario || nomeLegado || "",
      email: email || "",
      id_empresa: idEmpresaFinal ? _parseId(idEmpresaFinal) : null,
    };
  },
};

export default PdvDesktopAuthService;
